import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Choice, { Option } from '@components/choice/Choice'
import ModalSearch from '@components/modal/ModalSearch'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as SocieteStore from '@store/societe/societe'
import * as FamilleStore from '@store/famille/famille'
import * as UserStore from '@store/user/user'

import { FormikHelpers, useFormik } from 'formik'

interface Values {
  societePrestataireId: string
}

const Presta = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({ societePrestataireId: form.societePrestataireId }),
    [form.societePrestataireId]
  )

  // keep only enabled societes
  const societes = useReducer(SocieteStore.store, (s) => s.services)
  const services = React.useMemo(() => societes.filter((s) => s.enabled), [societes])

  const activities = useReducer(FamilleStore.store, (s) => s.activities)
  const user = useReducer(UserStore.store, (s) => s.user)

  const i18n = useI18n()

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  React.useEffect(() => {
    if (form.familles && !form.societePrestataireId) {
      // find all societeId in auto affect for selected families
      const servicesId = form.familles
        .map((id) => activities.find((f) => f.id === id))
        .reduce((acc, cur) => (cur ? [...acc, ...cur.autoAffectationSocieteIdList] : acc), [] as string[])

      const defaultService = servicesId.reverse().find((id) => services.find((s) => s.id === id))

      if (defaultService) {
        // if there is a default societe, affect it
        setFieldValue('societePrestataireId', defaultService)
      } else {
        // if not, select user id or none
        const userService = services.find((s) => user && user.societeId === s.id)

        if (userService) {
          // societe is known, affect it
          setFieldValue('societePrestataireId', userService.id)
        }
      }
    }
  }, [services.length, form.familles, form.societePrestataireId])

  const service = services.find((s) => s.id === values.societePrestataireId)

  const options: Option[] = [
    { label: i18n.t('screens.incident.PRESTA.service'), value: 'none' },
    ...services.map((s) => ({ label: s.nameSociete, value: s.id })),
  ]

  const option = {
    label: service ? service.nameSociete : i18n.t('screens.incident.PRESTA.service'),
    value: '',
    disabled: !service,
  }

  return (
    <MainContainer>
      <Animators>
        <Choice data={[option]} onItemSelected={() => null} value={service ? option : undefined} />
        <ModalSearch
          title={i18n.t('screens.incident.PRESTA.modify')}
          subtitle={i18n.t('screens.incident.PRESTA.current', { service: service ? service.nameSociete : '' })}
          data={options}
          onItemSelected={(item) => setFieldValue('societePrestataireId', item)}
          value={values.societePrestataireId}>
          <LinkText>{i18n.t('screens.incident.PRESTA.modify')}</LinkText>
        </ModalSearch>
      </Animators>
    </MainContainer>
  )
}

export default Presta

const MainContainer = styled(View)`
  margin: 0 20px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 0 : 150)}px;
`

const LinkText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  text-decoration: underline;
  margin: 16px 0;
`
